import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import Avatar from '../../components/Avatar'
import { supabase } from '../../lib/supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import { useFriends } from '../../hooks/useFriends'
import { useUserDirectory } from '../../hooks/useUserDirectory'
import { cn } from '../../lib/utils'

// Conversations list. One row per counterpart, newest activity first,
// with a last-message preview + unread count. Every accepted friend
// gets a row even with no history, so a chat can start straight from
// the inbox. Tapping a row opens /messages/<partnerId> (the thread).
export function ConversationsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const userId = user?.id || null
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const { resolve } = useUserDirectory()
  const { friends } = useFriends()

  const refetch = useCallback(async () => {
    if (!userId) return
    const { data } = await supabase
      .from('direct_messages')
      .select('id, sender_id, recipient_id, body, image_url, audio_url, scheduled_for, dispatched_at, created_at, read_at')
      .order('created_at', { ascending: false })
      .limit(500)

    // Group every live message by the *other* party. Skip not-yet-
    // dispatched scheduled rows belonging to someone else (shouldn't
    // happen given RLS, but mirrors the thread's visibility rule).
    const convos = new Map()
    for (const m of data || []) {
      const live = m.dispatched_at || !m.scheduled_for
      if (!live && m.sender_id !== userId) continue
      const other = m.sender_id === userId ? m.recipient_id : m.sender_id
      if (!other) continue
      let entry = convos.get(other)
      if (!entry) {
        entry = { partnerId: other, last: null, unread: 0 }
        convos.set(other, entry)
      }
      // First seen wins as "last" because rows arrive newest-first.
      if (!entry.last && live) entry.last = m
      if (m.recipient_id === userId && !m.read_at && live) entry.unread += 1
    }

    const list = Array.from(convos.values()).sort((a, b) => {
      const ta = a.last ? new Date(a.last.created_at).getTime() : 0
      const tb = b.last ? new Date(b.last.created_at).getTime() : 0
      return tb - ta
    })
    setRows(list)
  }, [userId])

  useEffect(() => {
    if (!userId) { setRows([]); setLoading(false); return undefined }
    let alive = true
    setLoading(true)
    const channel = supabase
      .channel(`dm-convos-${userId}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'direct_messages' },
        () => { if (alive) refetch() },
      )
      .subscribe()
    refetch().then(() => { if (alive) setLoading(false) })
    return () => { alive = false; supabase.removeChannel(channel) }
  }, [userId, refetch])

  // Friends with no message history yet ride below the real
  // conversations so a chat can start from an empty inbox.
  const displayRows = useMemo(() => {
    const have = new Set(rows.map((r) => r.partnerId))
    const seeded = friends
      .filter((f) => !have.has(f.other_user_id))
      .map((f) => ({ partnerId: f.other_user_id, last: null, unread: 0 }))
    return [...rows, ...seeded]
  }, [rows, friends])

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('messages.list.title')}
        </h1>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </div>
      )}

      {!loading && displayRows.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8" dir="auto">
          {t('messages.list.empty')}
        </p>
      )}

      {!loading && displayRows.length > 0 && (
        <Card>
          <CardContent className="p-0">
            {displayRows.map((row, idx) => (
              <ConversationRow
                key={row.partnerId}
                row={row}
                userId={userId}
                locale={locale}
                identity={resolve(row.partnerId, locale)}
                onClick={() => navigate(`/messages/${row.partnerId}`)}
                isLast={idx === displayRows.length - 1}
                t={t}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function ConversationRow({ row, userId, locale, identity, onClick, isLast, t }) {
  const name = identity.name || row.partnerId.slice(0, 8) + '…'
  const time = useMemo(() => {
    if (!row.last) return ''
    try {
      const d = new Date(row.last.created_at)
      const now = new Date()
      const sameDay = d.toDateString() === now.toDateString()
      return d.toLocaleString(locale === 'ar' ? 'ar-EG' : 'en-US',
        sameDay ? { timeStyle: 'short' } : { dateStyle: 'short' })
    } catch {
      return ''
    }
  }, [row.last, locale])

  const preview = useMemo(() => {
    if (!row.last) return t('messages.list.startHint')
    const m = row.last
    const prefix = m.sender_id === userId ? `${t('messages.thread.you')}: ` : ''
    const text = m.body || (m.image_url ? '📷' : m.audio_url ? '🎤' : '')
    return prefix + text
  }, [row.last, userId, t])

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full p-3 flex items-center gap-3 text-start hover:bg-accent transition-colors',
        !isLast && 'border-b border-border/60',
      )}
    >
      <Avatar userId={row.partnerId} profile={identity.profile} size={44} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-foreground truncate" dir="auto">{name}</p>
          {time && <span className="text-[11px] text-muted-foreground flex-shrink-0" dir="auto">{time}</span>}
        </div>
        <p className={cn(
          'text-[13px] truncate',
          row.unread > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
        )} dir="auto">
          {preview}
        </p>
      </div>
      {row.unread > 0 ? (
        <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-app-primary-500 text-white text-[11px] font-bold inline-flex items-center justify-center flex-shrink-0">
          {row.unread > 99 ? '99+' : row.unread}
        </span>
      ) : (
        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
      )}
    </button>
  )
}

export default ConversationsPage
